/* eslint-disable jsx-a11y/control-has-associated-label */
import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import {
  DataGridPro, useGridApiRef, gridFilteredSortedRowIdsSelector,
} from '@mui/x-data-grid-pro';
import { Tooltip as MuiTooltip, TextField, IconButton } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { FilterList } from '@mui/icons-material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import Drawer from '@mui/material/Drawer';
import Backdrop from '@mui/material/Backdrop';
import ClipLoader from 'react-spinners/ClipLoader';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import { useFetch } from './api';
import EventDetailDrawer from './EventDetailDrawer';
import { dataGridSx } from './Connections';
import {
  EVENT_CATEGORIES, EVENT_TYPES_BY_CATEGORY, SEVERITY_BY_TYPE,
  eventLabel, eventDefinition, eventCategory,
  severityChipClass, severityDotClass, severityRailColor,
} from './eventTypes';

dayjs.extend(utc);

// preset date-range button, mirroring ApiLogPage's DateFilterButton
function DateFilterButton({
  value, label, width = 'w-20', dateFilter, setDateFilter, setStartDate, setEndDate, setDateRange,
}) {
  return (
    <button
      type="button"
      className={`${width} h-8 px-3 rounded-lg text-sm font-medium transition-all duration-150 ${
        dateFilter === value
          ? 'bg-surface-900 text-surface-0 shadow-xs'
          : 'bg-surface-0 text-surface-600 border border-surface-200 hover:bg-surface-50 hover:border-surface-300'
      }`}
      onClick={() => {
        setDateFilter(value);
        const durations = {
          '14d': [14, 'days'], '7d': [7, 'days'], '24h': [1, 'days'], '1h': [1, 'hour'],
        };
        const [num, unit] = durations[value];
        setStartDate(dayjs().utc().subtract(num, unit));
        setEndDate(dayjs().utc());
        setDateRange([null, null]);
      }}
    >
      {label}
    </button>
  );
}

const TYPE_LABELS = {
  wms: 'WMS', cart: 'Cart', carrier: 'Carrier', freight: 'Freight', erp: 'ERP',
};

// shared Autocomplete look, lifted from ApiLogPage's filter pickers
const filterAutocompleteSx = {
  '& .MuiInputBase-root': {
    borderRadius: '8px',
    height: '36px',
    fontSize: '0.8125rem',
    fontFamily: '"Plus Jakarta Sans"',
    backgroundColor: '#fff',
  },
};
const filterAutocompleteSlotProps = {
  popper: { style: { width: '280px' }, placement: 'bottom-start' },
  paper: {
    sx: {
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      border: '1px solid #E7E5E4',
      mt: 0.5,
    },
  },
};

// one labelled Autocomplete row in the filters panel
function FilterField({
  label, options, value, placeholder, onChange, getOptionLabel, groupBy,
}) {
  return (
    <div>
      <span className="block text-xs font-medium text-surface-500 mb-1.5">{label}</span>
      <Autocomplete
        options={options}
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        getOptionLabel={getOptionLabel}
        groupBy={groupBy}
        renderInput={(params) => (
          <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            placeholder={placeholder}
            sx={{ '& .MuiOutlinedInput-input': { height: '15px' } }}
          />
        )}
        size="small"
        fullWidth
        slotProps={filterAutocompleteSlotProps}
        sx={filterAutocompleteSx}
      />
    </div>
  );
}

function Events(props) {
  const trackstar = useFetch();
  const { integrationLists, connectionData } = props;

  // read once at mount so the initial fetch uses the shared-link window with no
  // second fetch, mirroring ApiLogPage's defaultUrlParams pattern
  const defaultUrlParams = new URLSearchParams(window.location.search);
  const dateFilterParam = defaultUrlParams.get('date_filter');
  const startDateParam = defaultUrlParams.get('start_date');
  const endDateParam = defaultUrlParams.get('end_date');

  const [severityFilter, setSeverityFilter] = useState(
    ['important', 'info'].includes(defaultUrlParams.get('severity')) ? defaultUrlParams.get('severity') : 'all',
  );
  const [integrationFilter, setIntegrationFilter] = useState(defaultUrlParams.get('integration') || null);
  const [customerFilter, setCustomerFilter] = useState(defaultUrlParams.get('customer_id') || null);
  const [connectionFilter, setConnectionFilter] = useState(defaultUrlParams.get('connection') || null);
  const [typeFilter, setTypeFilter] = useState(defaultUrlParams.get('type') || null);
  const [dateFilter, setDateFilter] = useState(
    ['14d', '7d', '24h', '1h', 'custom'].includes(dateFilterParam) ? dateFilterParam : '7d',
  );
  const [startDate, setStartDate] = useState(
    startDateParam ? dayjs(startDateParam) : dayjs().utc().subtract(7, 'days'),
  );
  const [endDate, setEndDate] = useState(
    endDateParam ? dayjs(endDateParam) : dayjs().utc(),
  );
  const [dateRange, setDateRange] = useState(
    dateFilterParam === 'custom' && startDateParam && endDateParam
      ? [dayjs(startDateParam), dayjs(endDateParam)]
      : [null, null],
  );
  const [searchValue, setSearchValue] = useState('');
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [showLegend, setShowLegend] = useState(false);

  const [events, setEvents] = useState([]);
  const [attentionCount, setAttentionCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  const [showEventDetail, setShowEventDetail] = useState(false);
  const [eventDetail, setEventDetail] = useState(null);

  const gridApiRef = useGridApiRef();

  // reflect a filter change into the url so a filtered feed is shareable / back-forward safe
  const setUrlParam = (key, value, defaultValue) => {
    const params = new URLSearchParams(window.location.search);
    if (value && value !== defaultValue) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    const qs = params.toString();
    const target = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.pushState({}, '', target);
  };

  // ignore responses from superseded date-window requests so a slow older
  // fetch can't overwrite the current range (or a stale failure blank it)
  const latestFetchRef = useRef(0);
  // live handles for the popstate handler below (its closure is set up once on
  // mount, so it reads current rows and the current window through these refs)
  const rowsRef = useRef([]);
  const dateFilterRef = useRef(dateFilter);
  const startIsoRef = useRef(startDate.toISOString());
  const endIsoRef = useRef(endDate.toISOString());

  const PRESET_DURATIONS = {
    '14d': [14, 'days'], '7d': [7, 'days'], '24h': [1, 'days'], '1h': [1, 'hour'],
  };

  // only the date window is server-scoped; every other filter is applied
  // in-memory over the single loaded page. start_date/end_date supersede range.
  const fetchEvents = () => {
    latestFetchRef.current += 1;
    const requestId = latestFetchRef.current;
    setLoading(true);
    setFetchError(false);
    trackstar(`internal/events?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`)
      .then((response) => {
        if (requestId !== latestFetchRef.current) return;
        if (!response || response.error || !Array.isArray(response.data)) {
          setFetchError(true);
          setEvents([]);
          return;
        }
        // the API no longer sends severity or an attention count; derive both
        // from the event type here (SEVERITY_BY_TYPE), and enrich each event so
        // the rest of the page can read event.severity as before
        const enriched = [];
        let attention = 0;
        response.data.forEach((event) => {
          const severity = SEVERITY_BY_TYPE[event.type] || 'info';
          if (severity !== 'info') attention += 1;
          enriched.push({ ...event, severity });
        });
        setEvents(enriched);
        setAttentionCount(attention);
      })
      .catch(() => {
        if (requestId !== latestFetchRef.current) return;
        setFetchError(true);
        setEvents([]);
      })
      .finally(() => {
        if (requestId !== latestFetchRef.current) return;
        setLoading(false);
      });
  };

  // refetch when the date window moves (the sole server-scoped filter)
  useEffect(() => {
    fetchEvents();
  }, [startDate, endDate]);

  // filters push to the url via history, so on Back/Forward re-read the url
  // into state; popstate fires only on real navigation, not our own pushState
  useEffect(() => {
    const restoreFromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      const severity = params.get('severity');
      setSeverityFilter(['important', 'info'].includes(severity) ? severity : 'all');
      setIntegrationFilter(params.get('integration') || null);
      setCustomerFilter(params.get('customer_id') || null);
      setConnectionFilter(params.get('connection') || null);
      setTypeFilter(params.get('type') || null);
      const df = params.get('date_filter');
      const validDf = ['14d', '7d', '24h', '1h', 'custom'].includes(df) ? df : '7d';
      const sd = params.get('start_date');
      const ed = params.get('end_date');
      // presets store only date_filter (start/end are derived), so rebuild the
      // window from the preset; only touch it when it actually changed, so
      // navigating drawer state doesn't rebuild the dates and refetch the feed
      const isCustom = validDf === 'custom' && sd && ed;
      const windowChanged = validDf !== dateFilterRef.current
        || (isCustom && (sd !== startIsoRef.current || ed !== endIsoRef.current));
      if (windowChanged) {
        setDateFilter(validDf);
        if (isCustom) {
          setStartDate(dayjs(sd));
          setEndDate(dayjs(ed));
          setDateRange([dayjs(sd), dayjs(ed)]);
        } else {
          const [num, unit] = PRESET_DURATIONS[validDf] || [7, 'days'];
          setStartDate(dayjs().utc().subtract(num, unit));
          setEndDate(dayjs().utc());
          setDateRange([null, null]);
        }
      }

      // mirror ?id= into the drawer: close it when id is gone, reopen the
      // matching row when id comes back
      const eventId = params.get('id');
      if (!eventId) {
        setShowEventDetail(false);
        setEventDetail(null);
        return;
      }
      const match = rowsRef.current.find((row) => row.id === eventId);
      if (match) {
        setEventDetail(match);
        setShowEventDetail(true);
      } else {
        // the url names an event that isn't in the loaded rows, so we can't show
        // it; close rather than keep displaying a different event
        setShowEventDetail(false);
        setEventDetail(null);
      }
    };
    window.addEventListener('popstate', restoreFromUrl);
    return () => window.removeEventListener('popstate', restoreFromUrl);
  }, []);

  // integration display name for a given integration_name, looked up across types
  const displayNameFor = useCallback((integrationName, integrationType) => {
    if (!integrationLists) return integrationName;
    const typeList = integrationLists[integrationType];
    if (typeList && typeList[integrationName]) {
      return typeList[integrationName].display_name;
    }
    return integrationName;
  }, [integrationLists]);

  // distinct customer ids in the loaded set, for the customer filter dropdown
  const customerOptions = useMemo(() => {
    const seen = new Set();
    events.forEach((event) => {
      if (event.customer_id) seen.add(event.customer_id);
    });
    return Array.from(seen).sort();
  }, [events]);

  // integration options from the org's installed integrations (display + value),
  // mirroring how ApiLogPage builds its picker from integrationLists
  const integrationOptions = useMemo(() => {
    const built = [];
    if (integrationLists) {
      Object.values(integrationLists).forEach((typeList) => {
        Object.entries(typeList).forEach(([name, entry]) => {
          built.push({ value: name, label: entry.display_name || name });
        });
      });
    }
    built.sort((a, b) => a.label.localeCompare(b.label));
    return built;
  }, [integrationLists]);

  // connection ids from the connectionData prop, same source ApiLogPage uses
  const connectionOptions = useMemo(() => {
    if (!connectionData) return [];
    return connectionData.map((conn) => conn.connection_id);
  }, [connectionData]);

  // rows for the grid: apply every panel filter + the search box in-memory (AND),
  // then sort newest first. only the date range is server-scoped.
  const rows = useMemo(() => {
    const needle = searchValue.trim().toLowerCase();
    const built = [];
    events.forEach((event) => {
      const label = eventLabel(event.type);
      const displayName = displayNameFor(
        event.integration_name,
        event.integration_type,
      );

      if (severityFilter === 'important' && event.severity === 'info') return;
      if (severityFilter === 'info' && event.severity !== 'info') return;
      if (integrationFilter && event.integration_name !== integrationFilter) return;
      if (customerFilter && event.customer_id !== customerFilter) return;
      if (connectionFilter && event.connection_id !== connectionFilter) return;
      if (typeFilter && event.type !== typeFilter) return;

      if (needle) {
        const haystack = [
          label,
          event.message,
          event.customer_id,
          displayName,
          event.connection_id,
          event.type,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(needle)) return;
      }

      built.push({ id: event.id, displayName, ...event });
    });
    built.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return built;
  }, [
    events, displayNameFor, searchValue, severityFilter,
    integrationFilter, customerFilter, connectionFilter, typeFilter,
  ]);
  rowsRef.current = rows;

  const openEventDetail = (row) => {
    setEventDetail(row);
    setShowEventDetail(true);
    // if ?id= already points at this event (arrived via link/Back) don't push a
    // duplicate history entry — that dup makes browser Back stick
    if (new URLSearchParams(window.location.search).get('id') === row.id) return;
    setUrlParam('id', row.id, null);
  };

  const closeEventDetail = () => {
    setShowEventDetail(false);
    setUrlParam('id', null, null);
  };

  // panel-filter setters double as url writers so a filtered feed is shareable;
  // null clears the param
  const applySeverity = (value) => {
    setSeverityFilter(value);
    setUrlParam('severity', value === 'all' ? null : value, null);
  };
  const applyIntegration = (value) => {
    setIntegrationFilter(value);
    setUrlParam('integration', value, null);
  };
  const applyCustomer = (value) => {
    setCustomerFilter(value);
    setUrlParam('customer_id', value, null);
  };
  const applyConnection = (value) => {
    setConnectionFilter(value);
    setUrlParam('connection', value, null);
  };
  const applyType = (value) => {
    setTypeFilter(value);
    setUrlParam('type', value, null);
  };

  // preset window helper for the reset path; the toolbar buttons use the shared
  // DateFilterButton component (identical to ApiLogPage) instead
  const selectPreset = (value) => {
    const [num, unit] = PRESET_DURATIONS[value];
    setDateFilter(value);
    setStartDate(dayjs().utc().subtract(num, unit));
    setEndDate(dayjs().utc());
    setDateRange([null, null]);
  };

  // custom range picked: recompute the window (ApiLogPage's onChange, verbatim)
  const selectCustomRange = (newValue) => {
    setDateRange(newValue);
    if (newValue[0] && newValue[1]) {
      // cover the full range: start of the first day through end of the last day
      const start = newValue[0].startOf('day');
      const end = newValue[1].endOf('day');
      setStartDate(start);
      setEndDate(end);
      setDateFilter('custom');
    }
  };

  // keep the date window in the url so a chosen range is shareable: a preset
  // needs only its key (start/end are derived); custom needs both endpoints
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('date_filter', dateFilter);
    if (dateFilter === 'custom') {
      params.set('start_date', startDate.toISOString());
      params.set('end_date', endDate.toISOString());
    } else {
      params.delete('start_date');
      params.delete('end_date');
    }
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    dateFilterRef.current = dateFilter;
    startIsoRef.current = startDate.toISOString();
    endIsoRef.current = endDate.toISOString();
  }, [dateFilter, startDate, endDate]);

  // keep the drawer in sync with ?id= as the feed changes (initial load,
  // filter/search, date refetch). read the real url, not react-router location,
  // which doesn't see our pushState writes. show the selected event when it's in
  // the current rows, close when it isn't so the drawer never shows an event
  // that's no longer in the feed. Back/Forward is handled by the popstate sync.
  useEffect(() => {
    const eventId = new URLSearchParams(window.location.search).get('id');
    if (!eventId) return;
    const match = rows.find((row) => row.id === eventId);
    if (match) {
      openEventDetail(match);
    } else {
      setShowEventDetail(false);
      setEventDetail(null);
    }
  }, [rows]);

  // esc closes; up/down move between visible rows (respects sort + quick filter)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!showEventDetail) return;
      if (event.key === 'Escape') {
        closeEventDetail();
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const sortedRowIds = gridFilteredSortedRowIdsSelector(gridApiRef);
        const currentIndex = sortedRowIds.findIndex((id) => id === eventDetail?.id);
        if (currentIndex === -1) return;
        const newIndex = event.key === 'ArrowDown'
          ? Math.min(currentIndex + 1, sortedRowIds.length - 1)
          : Math.max(currentIndex - 1, 0);
        if (newIndex !== currentIndex) {
          const newRow = rows.find((r) => r.id === sortedRowIds[newIndex]);
          if (!newRow) return;
          openEventDetail(newRow);
          gridApiRef.current.scrollToIndexes({ rowIndex: newIndex });
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showEventDetail, rows, eventDetail, gridApiRef]);

  // active-filter count excludes the date range (its own control), matching ApiLog
  const activeFilterCount = [
    severityFilter !== 'all',
    integrationFilter,
    customerFilter,
    connectionFilter,
    typeFilter,
  ].filter(Boolean).length;

  // clears panel filters only; leaves the date range + search box alone
  const clearAllFilters = () => {
    applySeverity('all');
    applyIntegration(null);
    applyCustomer(null);
    applyConnection(null);
    applyType(null);
  };

  // empty-state escape hatch: clear everything, including search + date range
  const resetAllFilters = () => {
    clearAllFilters();
    setSearchValue('');
    selectPreset('7d');
  };

  // any narrowing at all, including search + a non-default window, drives empty-state copy
  const hasActiveFilters = activeFilterCount > 0 || searchValue || dateFilter !== '7d';

  const totalEvents = events.length;

  const colHeaders = [
    {
      field: 'event',
      headerName: 'Event',
      display: 'flex',
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => {
        const { row } = params;
        return (
          <div
            className="flex items-stretch gap-3 h-full w-full"
            style={{ borderLeft: `2px solid ${severityRailColor(row.severity)}`, marginLeft: '-16px', paddingLeft: '14px' }}
          >
            <div className="flex flex-col justify-center min-w-0 py-1">
              <div className="flex items-center gap-2">
                <MuiTooltip title={eventDefinition(row.type)} arrow placement="top">
                  <span className={`badge ${severityChipClass(row.severity)} shrink-0 cursor-help`}>
                    <span className={`status-dot ${severityDotClass(row.severity)}`} />
                    {eventLabel(row.type)}
                  </span>
                </MuiTooltip>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Time',
      display: 'flex',
      flex: 0.7,
      minWidth: 150,
      renderCell: (params) => (
        <span className="text-surface-600 text-xs whitespace-nowrap">
          {new Date(params.value).toLocaleString([], {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </span>
      ),
    },
    {
      field: 'connection_id',
      headerName: 'Connection ID',
      display: 'flex',
      flex: 1.2,
      minWidth: 170,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return <span className="text-surface-300">—</span>;
        return (
          <span className="font-mono text-xs text-surface-600 truncate">{params.value}</span>
        );
      },
    },
    {
      field: 'integration_name',
      headerName: 'Integration',
      display: 'flex',
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => {
        const { row } = params;
        const typeLabel = TYPE_LABELS[row.integration_type] || row.integration_type;
        let logo = `https://trackstarlogosbucket.s3.amazonaws.com/${row.integration_name}.png`;
        if (row.integration_name === 'sandbox') logo = 'ts_logo_2024.png';
        return (
          <div className="flex gap-2.5 items-center">
            <div className="w-7 h-7 rounded-md bg-surface-100 flex items-center justify-center border border-surface-200">
              <img src={logo} alt={`${row.integration_name} logo`} className="h-4 w-4" />
            </div>
            <div className="flex flex-col">
              <span className="text-surface-700 font-medium">{row.displayName}</span>
              <span className="text-[11px] text-surface-400">{typeLabel}</span>
            </div>
          </div>
        );
      },
    },
    {
      field: 'customer_id',
      headerName: 'Customer ID',
      display: 'flex',
      flex: 0.9,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return <span className="text-surface-300">—</span>;
        return (
          <span className="font-mono text-xs text-surface-600 truncate">{params.value}</span>
        );
      },
    },
  ];

  function renderNoRows() {
    if (fetchError) {
      return (
        <div className="no-rows-overlay">
          <div className="no-rows-overlay-icon">
            <svg className="w-6 h-6 text-error-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01M4.93 19h14.14a2 2 0 001.74-3l-7.07-12a2 2 0 00-3.48 0l-7.07 12a2 2 0 001.74 3z" />
            </svg>
          </div>
          <p className="no-rows-overlay-title">Unable to load events</p>
          <p className="no-rows-overlay-text">Something went wrong fetching events. Try refreshing.</p>
          <button type="button" className="btn btn-secondary" onClick={fetchEvents}>Refresh</button>
        </div>
      );
    }
    if (hasActiveFilters) {
      return (
        <div className="no-rows-overlay">
          <div className="no-rows-overlay-icon">
            <svg className="w-6 h-6 text-surface-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L14 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 018 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
            </svg>
          </div>
          <p className="no-rows-overlay-title">No events match these filters</p>
          <p className="no-rows-overlay-text">Try widening the date range or clearing a filter.</p>
          <button type="button" className="btn btn-secondary" onClick={resetAllFilters}>Clear filters</button>
        </div>
      );
    }
    return (
      <div className="no-rows-overlay">
        <div className="no-rows-overlay-icon">
          <svg className="w-6 h-6 text-surface-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <p className="no-rows-overlay-title">No events yet</p>
        <p className="no-rows-overlay-text">Once your users start connecting integrations, install and sync activity will show up here.</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-baseline gap-3">
            <h1 className="page-title">Events</h1>
            <span className="page-subtitle">A running log of important events across your organization</span>
          </div>
        </div>
      </div>

      {/* Data table section */}
      <div className="relative flex flex-col flex-1 overflow-hidden rounded-xl border border-surface-200 bg-surface-0 shadow-xs">
        {/* Table header */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-surface-50/50 border-b border-surface-200 gap-2">
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="Search events..."
              className="input w-64 !py-1.5 !text-sm"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
            {loading ? (
              <ClipLoader size={14} color="#78716C" />
            ) : (
              <p className="text-xs text-surface-500 whitespace-nowrap">
                <span className={`font-semibold ${attentionCount > 0 ? 'text-warning-dark' : 'text-surface-700'}`}>{attentionCount}</span>
                {' important'}
                <span className="mx-1.5 text-surface-300">•</span>
                <span className="font-semibold text-surface-700">{totalEvents}</span>
                {' total'}
              </p>
            )}
          </div>
          <div className="flex gap-2 items-center">
            {/* date-range presets, matching ApiLogPage order + labels */}
            <div className="flex gap-1.5">
              <DateFilterButton value="14d" label="14 days" width="w-20" dateFilter={dateFilter} setDateFilter={setDateFilter} setStartDate={setStartDate} setEndDate={setEndDate} setDateRange={setDateRange} />
              <DateFilterButton value="7d" label="7 days" width="w-20" dateFilter={dateFilter} setDateFilter={setDateFilter} setStartDate={setStartDate} setEndDate={setEndDate} setDateRange={setDateRange} />
              <DateFilterButton value="24h" label="24 hours" width="w-24" dateFilter={dateFilter} setDateFilter={setDateFilter} setStartDate={setStartDate} setEndDate={setEndDate} setDateRange={setDateRange} />
              <DateFilterButton value="1h" label="1 hour" width="w-20" dateFilter={dateFilter} setDateFilter={setDateFilter} setStartDate={setStartDate} setEndDate={setEndDate} setDateRange={setDateRange} />
            </div>
            {/* custom range picker */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div className="w-52">
                <DateRangePicker
                  slots={{ field: SingleInputDateRangeField }}
                  name="allowedRange"
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  sx={{
                    '& .MuiPickersTextField-root, & .MuiPickersOutlinedInput-root': {
                      borderRadius: '10px',
                      height: '32px',
                      fontSize: '0.8125rem',
                      fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E7E5E4',
                      '&:hover': { borderColor: '#D6D3D1' },
                      '& fieldset': { border: 'none' },
                    },
                  }}
                  disableFuture
                  minDate={dayjs().subtract(12, 'months')}
                  value={dateRange}
                  onChange={selectCustomRange}
                />
              </div>
            </LocalizationProvider>
            <div className="w-px h-6 bg-surface-200" />
            <button
              type="button"
              onClick={() => setShowFiltersPanel(!showFiltersPanel)}
              className={`flex items-center gap-2 px-3 h-8 rounded-lg text-sm font-medium transition-all border ${
                showFiltersPanel || activeFilterCount > 0
                  ? 'bg-surface-900 text-white border-surface-900'
                  : 'bg-surface-0 text-surface-600 border-surface-200 hover:bg-surface-50'
              }`}
            >
              <FilterList sx={{ fontSize: '16px' }} />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-white/20 text-xs">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <MuiTooltip title="What do these events mean?" arrow placement="top">
              <IconButton
                onClick={() => setShowLegend(!showLegend)}
                size="small"
                className={`border ${showLegend ? 'bg-surface-900 text-white border-surface-900' : 'text-surface-500 border-surface-200 hover:bg-surface-50'}`}
                sx={{ width: 32, height: 32, borderRadius: '8px' }}
              >
                <HelpOutlineIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </MuiTooltip>
          </div>
        </div>

        {/* Filters panel */}
        {showFiltersPanel && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setShowFiltersPanel(false)}
              onKeyDown={(e) => e.key === 'Escape' && setShowFiltersPanel(false)}
              role="button"
              tabIndex={0}
              aria-label="Close filters"
            />
            <div className="absolute right-4 top-14 z-50 bg-surface-0 rounded-xl border border-surface-200 shadow-xl p-5 w-80">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-semibold text-surface-800">Filters</span>
                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="text-xs text-surface-500 hover:text-surface-700"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-4">
                <div>
                  <span className="block text-xs font-medium text-surface-500 mb-1.5">Severity</span>
                  <div className="flex gap-1 p-1 bg-surface-100 rounded-lg">
                    {[
                      { value: 'all', label: 'All' },
                      { value: 'important', label: 'Important' },
                      { value: 'info', label: 'Informational' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => applySeverity(opt.value)}
                        className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-all cursor-pointer ${
                          severityFilter === opt.value
                            ? 'bg-surface-0 text-surface-900 shadow-sm'
                            : 'text-surface-500 hover:text-surface-700'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <FilterField
                  label="Integration"
                  options={integrationOptions}
                  value={integrationOptions.find((o) => o.value === integrationFilter) || null}
                  placeholder="All integrations"
                  getOptionLabel={(opt) => opt.label || ''}
                  onChange={(newValue) => applyIntegration(newValue ? newValue.value : null)}
                />
                <FilterField
                  label="Customer"
                  options={customerOptions}
                  value={customerFilter}
                  placeholder="All customers"
                  onChange={(newValue) => applyCustomer(newValue || null)}
                />
                <FilterField
                  label="Connection"
                  options={connectionOptions}
                  value={connectionFilter}
                  placeholder="All connections"
                  onChange={(newValue) => applyConnection(newValue || null)}
                />
                <FilterField
                  label="Event type"
                  options={EVENT_TYPES_BY_CATEGORY}
                  value={typeFilter}
                  placeholder="All event types"
                  getOptionLabel={(type) => eventLabel(type)}
                  groupBy={(type) => eventCategory(type)}
                  onChange={(newValue) => applyType(newValue || null)}
                />
              </div>
            </div>
          </>
        )}

        {/* Event-type drawer: browse the full dictionary and filter by type */}
        <Drawer
          anchor="right"
          open={showLegend}
          onClose={() => setShowLegend(false)}
          PaperProps={{
            sx: {
              width: '28rem',
              maxWidth: '90vw',
              backgroundColor: '#FFFFFF',
              borderLeft: '1px solid #E7E5E4',
              boxShadow: '-20px 0 40px -15px rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          <div className="flex flex-col h-full">
            <div className="sticky top-0 z-10 bg-surface-0/90 backdrop-blur-md border-b border-surface-200 px-5 py-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-surface-800">Event types</span>
              <IconButton onClick={() => setShowLegend(false)} size="small" sx={{ padding: '4px' }}>
                <CloseIcon sx={{ fontSize: '18px', color: '#78716C' }} />
              </IconButton>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {EVENT_CATEGORIES.map((category) => (
                <div key={category} className="mb-5">
                  <span className="block text-xs font-semibold uppercase tracking-wide text-surface-400 mb-2">{category}</span>
                  <div className="flex flex-col gap-3">
                    {EVENT_TYPES_BY_CATEGORY
                      .filter((type) => eventCategory(type) === category)
                      .map((type) => {
                        const severity = SEVERITY_BY_TYPE[type];
                        return (
                          <div key={type} className="flex flex-col gap-1">
                            <span className={`badge ${severityChipClass(severity)} w-fit`}>
                              <span className={`status-dot ${severityDotClass(severity)}`} />
                              {eventLabel(type)}
                            </span>
                            <span className="text-xs text-surface-500 leading-relaxed">{eventDefinition(type)}</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Drawer>

        {/* DataGrid */}
        <div className="flex-1 min-h-0">
          <DataGridPro
            rows={rows}
            columns={colHeaders}
            disableColumnMenu
            slots={{ noRowsOverlay: renderNoRows }}
            hideFooter
            rowHeight={40}
            columnHeaderHeight={40}
            sx={{
              ...dataGridSx,
              height: '100%',
              // the header's scrollbar-gutter filler is otherwise unstyled and
              // reads as a white gap on the right; color it like the header
              '& .MuiDataGrid-scrollbarFiller': { backgroundColor: '#FAFAF9 !important' },
              '& .MuiDataGrid-filler': { backgroundColor: '#FFFFFF !important' },
              // this key replaces the shared sx's columnHeaders block wholesale, so
              // restate the full header styling (matching the api-log page) and
              // size it to 40px so the header row has no leftover whitespace
              '& .MuiDataGrid-columnHeaders': {
                minHeight: '40px !important',
                maxHeight: '40px !important',
                fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
                fontWeight: 500,
                fontSize: '0.8125rem',
                color: '#78716C',
                borderBottom: '1px solid #E7E5E4',
                backgroundColor: '#FAFAF9',
              },
            }}
            initialState={{
              sorting: { sortModel: [{ field: 'created_at', sort: 'desc' }] },
            }}
            loading={loading}
            apiRef={gridApiRef}
            onRowClick={(params) => openEventDetail(params.row)}
            rowSelectionModel={{ type: 'include', ids: new Set(eventDetail ? [eventDetail.id] : []) }}
            hideFooterSelectedRowCount
          />
        </div>
      </div>

      {/* Drawer backdrop + drawer */}
      <Backdrop
        open={showEventDetail}
        onClick={closeEventDetail}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer - 1,
          backgroundColor: 'rgba(12, 10, 9, 0.4)',
          backdropFilter: 'blur(4px)',
        }}
      />
      <Drawer
        anchor="right"
        open={showEventDetail}
        onClose={closeEventDetail}
        PaperProps={{
          sx: {
            width: '55%',
            maxWidth: '800px',
            backgroundColor: '#FAFAF9',
            borderLeft: '1px solid #E7E5E4',
            boxShadow: '-20px 0 40px -15px rgba(0, 0, 0, 0.1)',
          },
        }}
        variant="persistent"
      >
        <EventDetailDrawer
          event={eventDetail}
          closeDrawer={closeEventDetail}
        />
      </Drawer>
    </div>
  );
}

export default Events;
